import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TO_API, KIND_COLORS, KIND_LABELS } from '../TimeOff';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');

function Month({ year, m, days }) {
  const first = new Date(year, m, 1);
  const count = new Date(year, m + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday-first
  const cells = [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`to-cal-month-${m + 1}`}>
      <p className="text-sm font-semibold text-slate-800 mb-2">{MONTHS[m]}</p>
      <div className="grid grid-cols-7 gap-0.5 text-[10px] text-slate-500 mb-1">{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i} className="text-center">{d}</span>)}</div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const key = `${year}-${pad(m + 1)}-${pad(d)}`;
          const info = days[key];
          const weekend = (lead + d - 1) % 7 >= 5;
          return (
            <span key={i} title={info ? `${KIND_LABELS[info.kind]}${info.note ? ' — ' + info.note : ''}` : key}
              data-testid={info ? `to-cal-day-${key}` : undefined}
              className={`h-6 flex items-center justify-center rounded text-[11px] ${info ? KIND_COLORS[info.kind] + ' font-semibold' : weekend ? 'text-slate-700' : 'text-slate-700'}`}>{d}</span>
          );
        })}
      </div>
    </div>
  );
}

export default function YearCalendar({ me, user }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [empId, setEmpId] = useState(user?.id);
  const [people, setPeople] = useState([]);
  const [data, setData] = useState(null);
  const canPick = me.is_hr || me.is_manager;

  useEffect(() => {
    if (!canPick) return;
    axios.get(`${TO_API}/balances`, { params: { year } }).then(r => setPeople(r.data.employees || [])).catch(() => {});
  }, [canPick, year]);
  useEffect(() => {
    axios.get(`${TO_API}/calendar`, { params: { year, employee_id: empId } }).then(r => setData(r.data)).catch(() => toast.error('Could not load calendar'));
  }, [year, empId]);

  const b = data?.balance;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <button onClick={() => setYear(y => y - 1)} className="p-2 rounded-lg bg-slate-900/5 text-slate-900" data-testid="to-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-slate-900 font-semibold text-lg" data-testid="to-cal-year">{year}</span>
        <button onClick={() => setYear(y => y + 1)} className="p-2 rounded-lg bg-slate-900/5 text-slate-900" data-testid="to-cal-next"><ChevronRight className="w-4 h-4" /></button>
        {canPick && (
          <select value={empId} onChange={e => setEmpId(e.target.value)} className="px-3 py-2 rounded-lg text-sm border border-slate-300 min-w-[200px]" data-testid="to-cal-employee">
            {!people.some(p => p.employee_id === user?.id) && <option value={user?.id}>{user?.full_name || 'Me'}</option>}
            {people.map(p => <option key={p.employee_id} value={p.employee_id}>{p.employee_name}</option>)}
          </select>
        )}
        {b && <span className="text-sm text-slate-800 ml-auto" data-testid="to-cal-balance">Vacation {b.used}/{b.total} used · {b.remaining - b.pending} left · {b.sick_used} sick</span>}
      </div>
      <div className="flex gap-3 flex-wrap text-xs text-slate-800">
        {Object.entries(KIND_LABELS).map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${KIND_COLORS[k].split(' ')[0]}`} />{l}</span>)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="to-year-grid">
        {MONTHS.map((_, m) => <Month key={m} year={year} m={m} days={data?.days || {}} />)}
      </div>
    </div>
  );
}
